/* eslint-disable complexity */
/* eslint-disable sort-keys */
import {ProductOfferPrice, ProductOfferProductSet} from 'models';
import {REVIEWS_PAGE_SIZE, SAFE_PRODUCT_PAGE_LOCAL_STORAGE_KEY} from 'modules/products/constants';
import {REDIRECT_REQUEST_COOKIE_NAME} from 'core/constants';
import {TBeforePopStateCallback} from 'core/types';
import {ILocalStorageSafeProductData, IProductSection, IProductShort, IProperty} from 'modules/products/types';
import {CookieService} from 'core/services/CookieService';
import {PaginationUrlService} from 'plugins/modules/pagination/services/PaginationUrlService';
import {getMediaSrc} from 'core/helpers/media';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {BRAND, FINE_RARE_ITEM, YEAR} from 'modules/product-card/constants/characteristics';
import {PRODUCT_CATEGORIES_CODES_SORTED, SECTION_CODE} from 'modules/product-card/constants';
import {ESortingActions} from 'modules/products/providers/CatalogPageProvider/types';
import {APP_ROUTES_TO_URL} from 'core/modules/routing/constants';

export const countTotal = (priceData: ProductOfferPrice, productSet: ProductOfferProductSet[]) => {
    const main = {...priceData.main};

    productSet.forEach((productSetItem) =>
        productSetItem.variants.forEach((variant) => {
            if (variant.isSelected && main.basePrice) {
                main.price += variant.price.price;
                main.basePrice += variant.price.price;
            } else {
                main.price += variant.price.price;
            }
        }),
    );
    return {
        alternative: null,
        main,
    } as ProductOfferPrice;
};

export const onListingHistoryPopState: TBeforePopStateCallback = ({as}) => {
    const restoringProductData: ILocalStorageSafeProductData = JSON.parse(
        localStorage.getItem(SAFE_PRODUCT_PAGE_LOCAL_STORAGE_KEY) || '{}',
    );

    if (!restoringProductData.productPage) {
        return true;
    }

    const rewriteUrl = new URL(window.location.origin + as);
    rewriteUrl.searchParams.set(
        PaginationUrlService.COMMON_PARAM_NAMES.PAGE.toString(),
        restoringProductData.productPage,
    );

    const cookieService = CookieService.getInstance();
    cookieService.setInsecureCookie(REDIRECT_REQUEST_COOKIE_NAME, rewriteUrl.toString());

    return true;
};

// TODO: убрать метод transformUnitToSet, когда будет доделан бэк
export const transformUnitToSet = (productSetTotalPrice: ProductOfferPrice) => {
    return {...productSetTotalPrice, main: {...productSetTotalPrice.main, unit: 'компл.'}};
};

export const makeGallerySources = (src: string) => {
    return {
        ['xl']: `${getMediaSrc(src, {height: 604})} 1x, ${getMediaSrc(src, {height: 1208})} 2x`,
        ['lg']: `${getMediaSrc(src, {height: 460})} 1x, ${getMediaSrc(src, {height: 920})} 2x`,
        ['sm']: `${getMediaSrc(src, {height: 248})} 1x, ${getMediaSrc(src, {height: 496})} 2x`,
    };
};

export const makeGastronomySources = (src: string) => {
    return {
        ['xl']: `${getMediaSrc(src, {height: 128, width: 128})} 1x, ${getMediaSrc(src, {height: 256, width: 256})} 2x`,
        ['lg']: `${getMediaSrc(src, {height: 96, width: 96})} 1x, ${getMediaSrc(src, {height: 192, width: 192})} 2x`,
    };
};

export const getProductFineAndRarePropValue = (properties: IProperty[] | Record<string, any> = []): Boolean => {
    if (Array.isArray(properties)) {
        if (!properties.length) {
            return false;
        }

        const propertyFineAndRare = properties.find(({code}) => code === FINE_RARE_ITEM.KEY);

        if (!propertyFineAndRare) {
            return false;
        }
        return Boolean(propertyFineAndRare.value);
    }
    return false;
};

export const isProductFineAndRare = (product?: Partial<IProduct | IProductShort> | null) => {
    if (!product) {
        return false;
    }

    return getProductFineAndRarePropValue(product.properties);
};

export const getProductSectionCodes = (sections?: IProductSection[]) => {
    const sectionsCodeList: string[] = [];
    if (Array.isArray(sections)) {
        sections.forEach(({code, parent}) => {
            sectionsCodeList.push(code);

            if (parent) {
                let section: IProductSection | null = parent;

                while (section) {
                    sectionsCodeList.push(section.code);
                    section = section.parent;
                }
            }
        });
    }
    return sectionsCodeList;
};

export const getProductSpecialCategory = (product?: IProduct) => {
    if (!product) {
        return '';
    }

    if (isProductFineAndRare(product)) {
        return SECTION_CODE.FINE_AND_RARE;
    }

    const sectionsCodeList: string[] = [];

    if (product.topSection === SECTION_CODE.AKSESSUARY) {
        return SECTION_CODE.AKSESSUARY;
    }

    if (Array.isArray(product.sections)) {
        product.sections.forEach(({code, parent}) => {
            sectionsCodeList.push(code);

            if (parent) {
                let section: IProductSection | null = parent;

                while (section) {
                    sectionsCodeList.push(section.code);
                    section = section.parent;
                }
            }
        });
    }

    const {
        AKSESSUARY,
        IGRISTOE_VINO,
        SHAMPANSKOE,
        KONYAK,
        KSN,
        MINERALNAYA_VODA,
        SAKE,
        STEKLO,
        VINO,
        VISKY,
        VODA_I_SOKI,
    } = SECTION_CODE;

    if (sectionsCodeList.includes(SAKE)) {
        return SAKE;
    }

    if (sectionsCodeList.includes(VISKY)) {
        return VISKY;
    }

    if (sectionsCodeList.includes(KONYAK)) {
        return KONYAK;
    }

    if (sectionsCodeList.includes(KSN)) {
        return KSN;
    }

    if (sectionsCodeList.includes(IGRISTOE_VINO)) {
        return IGRISTOE_VINO;
    }

    if (sectionsCodeList.includes(VINO)) {
        return VINO;
    }

    if (sectionsCodeList.includes(MINERALNAYA_VODA)) {
        return MINERALNAYA_VODA;
    }

    if (sectionsCodeList.includes(VODA_I_SOKI)) {
        return VODA_I_SOKI;
    }

    if (sectionsCodeList.includes(STEKLO)) {
        return STEKLO;
    }

    if (sectionsCodeList.includes(AKSESSUARY)) {
        return AKSESSUARY;
    }

    if (sectionsCodeList.includes(SHAMPANSKOE)) {
        return SHAMPANSKOE;
    }

    return '';
};

export const getProductCatalogPath = (product: IProduct): string => {
    const {topSection, canonSection} = product;

    if (topSection !== canonSection) {
        return `/catalog/${topSection}/${canonSection}`;
    }

    return `/catalog/${canonSection}`;
};

export const getProductCategories = (product: IProduct): IProductSection[] => {
    const sectionsList: IProductSection[] = [];

    if (Array.isArray(product.sections)) {
        product.sections.forEach((category) => {
            sectionsList.push(category);

            const parentSection = category.parent;

            if (parentSection) {
                let section: IProductSection | null = parentSection;

                while (section) {
                    sectionsList.push(section);
                    section = section.parent;
                }
            }
        });
    }

    return sectionsList;
};

export const getProductSpecialCategoryData = (product: IProduct): IProductSection | undefined => {
    const sectionsList: IProductSection[] = getProductCategories(product);

    let category;

    for (const categoryCode of PRODUCT_CATEGORIES_CODES_SORTED) {
        const section = sectionsList.find(({code}) => code === categoryCode);

        if (section) {
            category = section;

            break;
        }
    }

    return category;
};

export const getProductBrandUrl = (code: string = '') => {
    return `${APP_ROUTES_TO_URL.brands}/${code}/`;
};

export const makeBrandLogoSources = (src: string) => {
    return {
        ['lg']: `${getMediaSrc(src, {height: 106, width: 169})} 1x, ${getMediaSrc(src, {height: 212, width: 338})} 2x`,
        ['sm']: `${getMediaSrc(src, {height: 88, width: 160})} 1x, ${getMediaSrc(src, {height: 176, width: 320})} 2x`,
    };
};

export const getDefaultReviewsParams = (code: string) => ({
    filter: {code: code as string, rating: null},
    pageNumber: 1,
    pageSize: REVIEWS_PAGE_SIZE,
    sort: ESortingActions.POPULAR,
});

export const getProductBrand = (product: IProduct): string => {
    const brandProperty = Array.isArray(product.properties)
        ? product.properties.find((property) => property.code === BRAND.KEY)
        : undefined;
    return 'object' === typeof brandProperty?.value && null !== brandProperty?.value
        ? String((brandProperty.value as any).translate ?? '')
        : String(brandProperty?.value ?? '');
};

export const getProductYear = (product: IProduct) => {
    const yearProperty = product.properties.find((property) => property.code === YEAR.KEY);
    return yearProperty?.value;
};

export const getProductTopSection = (section: IProductSection): IProductSection | null => {
    if (section.isTop) {
        return section;
    }
    if (section.parent) {
        return getProductTopSection(section.parent);
    }
    return null;
};

export const getProductCanonSection = (sections: IProductShort['sections'] = []) => {
    return sections?.find(({isCanonical}) => isCanonical) ?? sections[0];
};
